import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { tapResponse } from "@ngrx/operators";
import { DeepSignal, getState, patchState, StateSource, WritableStateSource } from "@ngrx/signals";
import { rxMethod, RxMethod } from "@ngrx/signals/rxjs-interop";
import { TranslateService } from "@ngx-translate/core";
import { ConfigService } from "@ph-core/services/config/config.service";
import { PhDialog } from "@ph-core/services/dialog";
import { FormService } from "@ph-core/services/form.service";
import { QuoteContainerService } from "@ph-core/services/quote-container.service";
import { LazyTemplateService } from "@ph-core/services/template/lazy-template.service";
import { CheckoutFormValues } from "@ph-model/checkout/checkout-form-values.model";
import { UserResponse } from "@ph-model/login";
import { PhNotificationComponent } from "@ph-shared/components/notification/notification/notification.component";
import { PhNotificationType } from "@ph-shared/components/notification/notification/notification.interface";
import { CartItem, LoadQuote, SaveQuote } from "@ph-shopping/model/cart";
import { CartItemErrorTypes } from "@ph-shopping/model/cart/cart-item.model";
import { GetProductsRequest } from "@ph-shopping/model/product.request";
import { ShoppingFlowType } from "@ph-shopping/model/shopping-flow-type.enum";
import { ShoppingStepsUrls } from "@ph-shopping/model/shopping-step-urls.model";
import { ShoppingStepperStoreType } from "@ph-shopping/modules/shopping-stepper/store/shopping-stepper.store";
import { ProductState } from "@ph-shopping/store/product/product.store";
import { VehicleStoreType } from "@ph-shopping/store/vehicle/vehicle.store";
import { PrismicStoreType } from "@ph-store/prismic/prismic.store";
import { UserStoreType } from "@ph-store/user/user.store";
import { catchError, filter, Observable, of, pipe, switchMap, tap, throwError } from "rxjs";
import { CartApiClient } from "../cart-api.client";
import { CartState } from "../cart-store.types";

export interface CartStoreQuoteForMethods extends WritableStateSource<object> {
    checkoutFormValues: DeepSignal<CheckoutFormValues>;
}

export interface CartStoreQuoteDeps {
    userStore: UserStoreType;
    vehicleStore: VehicleStoreType;
    httpClient: HttpClient;
    quoteContainer: QuoteContainerService;
    shoppingStepperStore: ShoppingStepperStoreType;
    router: Router;
    formService: FormService;
    cartApiClient: CartApiClient;
    dialog: PhDialog;
    translate: TranslateService;
    prismicStore: PrismicStoreType;
    templateService: LazyTemplateService;
}

export interface CartStoreQuoteMethodsReturn {
    loadQuote: RxMethod<void>;
    saveQuote: RxMethod<{ productState: ProductState }>;
    loadQuoteSuccess: (quote: LoadQuote) => void;
    callLoadQuote: (user: UserResponse, vin: string) => Observable<LoadQuote>;
}

export function withCartStoreQuoteMethods(
    store: CartStoreQuoteForMethods,
    deps: CartStoreQuoteDeps
): CartStoreQuoteMethodsReturn {
    const {
        userStore,
        vehicleStore,
        httpClient,
        quoteContainer,
        shoppingStepperStore,
        router,
        formService,
        cartApiClient, dialog, translate,
        prismicStore,
        templateService,
    } = deps;
    const environment = ConfigService.getEnvironment();

    const loadQuote = rxMethod<void>(
        pipe(
            switchMap(() => {
                const user = userStore.user();
                if (!user) {
                    console.error('User not found. Load quote aborted.');
                    return of([]);
                }
                return callLoadQuote(user, vehicleStore.vin()?.vin).pipe(
                    tapResponse<LoadQuote>({
                        next: (quote: LoadQuote) => loadQuoteSuccess(quote),
                        error: (error: unknown) => {
                            console.error(error);
                            const errorMessage =
                                (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string'
                                    ? error.error
                                    : String(error)) || '';
                            patchState(store, { error: errorMessage, success: false, savedQuote: false });
                        },
                    })
                );
            })
        )
    );

    const callLoadQuote = (user: UserResponse, vin: string): Observable<LoadQuote> => {
        const request = { dealer: user.cmsDealerNumber, vin };

        const useQuoteOnlyEndpoint: boolean = environment.features.useQuoteOnlyEndpoint;

        const apiUrl = environment.apiUrl;
        const url = user?.isRelatedDealer || useQuoteOnlyEndpoint ? `${apiUrl}/getQuoteOnly` : `${apiUrl}/getQuote`;

        return httpClient.post<LoadQuote>(url, request).pipe(catchError((error) => throwError(() => error)));
    };

    const loadQuoteSuccess = (quote: LoadQuote): void => {
        patchState(store, {
            validatedCart: quote.cartState.validatedCart.map((cart: CartItem) => ({
                ...cart,
                message:
                    cart.message && Array.isArray(cart.message)
                        ? [...cart.message.flat()]
                        : [cart.message as unknown as string],
                errorType:
                    cart.errorType && Array.isArray(cart.errorType)
                        ? [...cart.errorType.flat()]
                        : [cart.errorType as unknown as CartItemErrorTypes],
            })),
            cart: quote.cartState.cart.map((cart: CartItem) => ({
                ...cart,
                message:
                    cart.message && Array.isArray(cart.message)
                        ? [...cart.message.flat()]
                        : [cart.message as unknown as string],
                errorType:
                    cart.errorType && Array.isArray(cart.errorType)
                        ? [...cart.errorType.flat()]
                        : [cart.errorType as unknown as CartItemErrorTypes],
            })),
            submittedCart: quote.cartState.submittedCart,
            total: quote.cartState.total,
            success: quote.cartState.success,
            error: quote.cartState.error,
            checkoutFormValues: quote.cartState.checkoutFormValues,
            customerPostal: quote.cartState.customerPostal,
            cosignerPostal: quote.cartState.cosignerPostal,
            cosignerFormEnable: quote.cartState.cosignerFormEnable,
            checkoutForms: quote.cartState.checkoutForms,
            submittedCartSuccess: false,
            checkoutCartContainsError: false,
            savedQuote: true,
            requestedQuote: quote.cartState.requestedQuote,
            cartTaxFormValues: quote.cartState['cartTaxFormValues'] ? quote.cartState['cartTaxFormValues'] : [],
        });
        quoteContainer.setQuote(quote);

        // change cartFound value to false as this quote was loaded and not decoded manually
        quote.VehicleState.vin.cartFound = false;
        vehicleStore.loadVehicleQuote(quote);

        if (environment.features.useQuoteOnlyEndpoint) {
            const name: ShoppingFlowType = quote.VehicleState.vehicleFormEnabled
                ? ShoppingFlowType.Ancillary
                : ShoppingFlowType.PowerUp;
            shoppingStepperStore.initializeSteps(environment.shoppingFlow.steps);
            shoppingStepperStore.switchShoppingPath(name);
            shoppingStepperStore.markAllStepsAsValidAndVisited();
            shoppingStepperStore.switchToStepByUrl(ShoppingStepsUrls.CartSummary);
        } else {
            // navigate to product page
            void router.navigate(['/home/shopping']);
        }
    }

    const saveQuote = rxMethod<{ productState: ProductState }>(
        pipe(
            switchMap(({ productState }) => {
                const user = userStore.user();
                if (!user) {
                    console.error('User not found. Save quote aborted.');
                    return of([]);
                }
                let generatedProductRequest: GetProductsRequest;

                if (!Object.keys(productState.productsRequest).length && environment.features.shopping.supportChargerFlow) {
                    generatedProductRequest = GetProductsRequest.setGetProductsRequestByState(
                        user,
                        store.checkoutFormValues.customer(),
                        vehicleStore.values(),
                        vehicleStore.chargerForm()
                    );
                } else {
                    generatedProductRequest = productState.productsRequest;
                }

                const values = vehicleStore.values();
                const userState = getState(userStore);

                const quote = new SaveQuote(
                    generatedProductRequest,
                    {
                        ...getState(vehicleStore),
                        values: {
                            ...values,
                            vin:
                                values?.vin ||
                                values?.pid ||
                                values?.hin ||
                                values?.tin ||
                                vehicleStore.chargerForm()?.serialNumber,
                        },
                    },
                    getState(store as StateSource<CartState>),
                    userState
                );

                if (user.isRelatedDealer) {
                    quote.productState = productState;
                }
                quote.cartState.checkoutFormValues.customer.postalCode
                    ? formService.getFormattedPostalCode(quote.cartState.checkoutFormValues.customer.postalCode)
                    : null;
                quote.cartState.checkoutFormValues.cosigner.postalCode = quote.cartState.checkoutFormValues.cosigner
                    .postalCode
                    ? formService.getFormattedPostalCode(quote.cartState.checkoutFormValues.cosigner.postalCode)
                    : null;

                if (
                    Object.keys(quote.cartState.checkoutFormValues.lienholder || {}).length > 0 &&
                    quote.cartState.checkoutFormValues.lienholder
                ) {
                    quote.cartState.checkoutFormValues.lienholder.postalCode
                        ? formService.getFormattedPostalCode(quote.cartState.checkoutFormValues.lienholder.postalCode)
                        : null;
                }

                return cartApiClient.saveQuote(quote).pipe(
                    switchMap(() =>
                        dialog
                            .open(PhNotificationComponent, {
                                data: {
                                    type: PhNotificationType.approved,
                                    actions: [
                                        {
                                            text: translate.instant('quoteSavedPopup.print'),
                                            act: (ref) => ref.close({ open: true }),
                                        },
                                        { text: translate.instant('quoteSavedPopup.continue'), act: (ref) => ref.close() },
                                    ],
                                    value: {
                                        title: translate.instant('quoteSavedPopup.title'),
                                        message: translate.instant('quoteSavedPopup.body'),
                                    },
                                },
                            })
                            .afterClosed()
                    ),
                    filter(Boolean),
                    tap(() => {
                        const isFrenchSelected: boolean = localStorage.getItem('languageCode') === 'fr-CA';
                        const data = prismicStore.data();

                        const logoUrl = isFrenchSelected
                            ? data?.small_logo_fr.url || data?.small_logo.url
                            : data?.small_logo.url;

                        void templateService.printQuote(logoUrl);
                    })
                );
            })
        )
    );

    return {
        loadQuote,
        saveQuote,
        loadQuoteSuccess,
        callLoadQuote
    }
}